import React from 'react'
import { Helmet } from 'react-helmet-async'

interface SEOHeadProps {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: string
  // Article-specific fields for JSON-LD structured data
  publishedAt?: string
  updatedAt?: string
  author?: string
  tags?: string[]
}

const SITE_NAME = 'CodeToByte'
const DEFAULT_DESCRIPTION =
  'CodeToByte — a terminal-aesthetic tech blog exploring code, systems, and bytes.'
const DEFAULT_IMAGE = '/og-image.png'
const SITE_URL = 'https://www.codetobyte.com'

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  publishedAt,
  updatedAt,
  author,
  tags,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME
  const fullImage = image.startsWith('http') ? image : `${SITE_URL}${DEFAULT_IMAGE}`
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL

  const articleJsonLd =
    type === 'article'
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: title ?? SITE_NAME,
          description: description,
          image: fullImage,
          url: fullUrl,
          datePublished: publishedAt,
          dateModified: updatedAt ?? publishedAt,
          author: author
            ? { '@type': 'Person', name: author }
            : { '@type': 'Organization', name: SITE_NAME },
          publisher: {
            '@type': 'Organization',
            name: SITE_NAME,
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon.svg` },
          },
          keywords: tags?.join(', '),
        })
      : null

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* JSON-LD structured data for article rich snippets */}
      {articleJsonLd && (
        <script type="application/ld+json">{articleJsonLd}</script>
      )}
    </Helmet>
  )
}
